import { useState } from 'react';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export function useWorkOrderParser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseWorkOrder = async (text: string) => {
    setLoading(true);
    setError(null);

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: `You are a work order parser specialized in appliance service work orders. Extract information from service work orders and return it as JSON with the following structure:
{
  "firstName": "string",
  "lastName": "string",
  "address": "string",
  "city": "string", 
  "state": "string",
  "zipCode": "string",
  "phone": "string",
  "email": "string",
  "serviceType": "string",
  "modelNumber": "string",
  "serialNumber": "string",
  "svo": "string",
  "description": "string",
  "brand": "string",
  "notes": "string"
}

Rules:
- Split customer name into first and last name
- Format phone numbers as (XXX) XXX-XXXX
- Extract issue/resolution into description
- Include any parts information in notes
- Preserve exact model/serial numbers
- Identify brand from product type or explicit brand mention
- Handle multiple addresses by using the service location address
- Default serviceType to "Appliance" if not specified
- Extract brand from product type (e.g., "Range Hoods" -> brand lookup)
- Include parts list in notes field
- Use Parts Shipped To address if different from Service Location`
          },
          {
            role: "user",
            content: text
          }
        ],
        response_format: { type: "json_object" }
      });

      const parsed = JSON.parse(completion.choices[0]?.message?.content || '{}');
      
      // Set default service type
      if (!parsed.serviceType) {
        parsed.serviceType = 'Appliance';
      }

      // Clean up phone numbers
      if (parsed.phone) {
        parsed.phone = parsed.phone.replace(/[^\d]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      }

      // Format notes to include parts if present
      if (parsed.notes) {
        const parts = text.match(/Part No\.\s+Description\s+Quantity\s+Shipping Status([^]*?)(?=\n\s*\n|$)/);
        if (parts) {
          parsed.notes = `${parsed.notes}\n\nParts:\n${parts[1].trim()}`;
        }
      }

      // Validate required fields
      if (!parsed.firstName || !parsed.lastName || !parsed.address || !parsed.city || 
          !parsed.state || !parsed.zipCode || !parsed.phone) {
        throw new Error('Missing required customer information');
      }

      // Clean up empty strings
      Object.keys(parsed).forEach(key => {
        if (parsed[key] === '') {
          parsed[key] = null;
        }
      });

      return parsed;
    } catch (err) {
      console.error('Error parsing work order:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse work order');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { parseWorkOrder, loading, error };
}